import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { errText } from "../lib/api";
import { stageLabel, SERVICE_TYPES, confBadge } from "../lib/constants";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Plus, PaperPlaneTilt, Star, PenNib, CheckCircle, Eye, Sparkle, EnvelopeSimpleOpen } from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";
import { DocPreviewDialog } from "../components/DocPreview";
import { DossierPane } from "../components/DossierPane";
import { ComplianceLockCard } from "../components/ComplianceLockCard";
import { PriorYearImport } from "../components/PriorYearImport";
import PortalTabOrderPanel from "../components/firm/PortalTabOrderPanel";

export default function ClientDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const qc = useQueryClient();
  const [engOpen, setEngOpen] = useState(false);
  const [engForm, setEngForm] = useState({ service_type: "1040 Individual", tax_year: "2025", deadline: "" });
  const [msg, setMsg] = useState("");
  const [esignOpen, setEsignOpen] = useState(false);
  const [esignForm, setEsignForm] = useState({ engagement_id: "", title: "Form 8879 — e-file authorization", doc_kind: "8879", sign_method: "in_app", external_link: "" });
  const [previewDoc, setPreviewDoc] = useState(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: "", name: "" });

  const { data: c } = useQuery({ queryKey: ["client", id], queryFn: () => api.get(`/clients/${id}`).then((r) => r.data) });

  const createEng = useMutation({
    mutationFn: () => api.post("/engagements", { client_id: id, ...engForm }),
    onSuccess: () => { toast.success("Engagement created"); setEngOpen(false); qc.invalidateQueries({ queryKey: ["client", id] }); qc.invalidateQueries({ queryKey: ["engagements"] }); },
    onError: (e) => toast.error(errText(e)),
  });

  const sendMsg = useMutation({
    mutationFn: () => api.post("/messages", { client_id: id, body: msg }),
    onSuccess: () => { setMsg(""); qc.invalidateQueries({ queryKey: ["client", id] }); },
    onError: (e) => toast.error(errText(e)),
  });

  const createEnvelope = useMutation({
    mutationFn: () => api.post("/envelopes", { client_id: id, ...esignForm }),
    onSuccess: () => { toast.success("Signature request sent to the client portal"); setEsignOpen(false); qc.invalidateQueries({ queryKey: ["client", id] }); },
    onError: (e) => toast.error(errText(e)),
  });

  const markSigned = useMutation({
    mutationFn: (envId) => api.patch(`/envelopes/${envId}/mark-signed`),
    onSuccess: () => { toast.success("Marked signed — Drake Portals completion recorded"); qc.invalidateQueries({ queryKey: ["client", id] }); },
    onError: (e) => toast.error(errText(e)),
  });

  const resendWelcome = useMutation({
    mutationFn: () => api.post(`/clients/${id}/resend-welcome`),
    onSuccess: (r) => toast.success(`Welcome email re-sent to ${r.data.to}`),
    onError: (e) => toast.error(errText(e)),
  });

  const invitePortal = useMutation({
    mutationFn: () => api.post(`/clients/${id}/invite`, inviteForm),
    onSuccess: (r) => {
      toast.success(`Activation link emailed to ${r.data.email} — expires in ${r.data.expires_in_days} days`);
      setInviteOpen(false);
      setInviteForm({ email: "", name: "" });
      qc.invalidateQueries({ queryKey: ["client", id] });
    },
    onError: (e) => toast.error(errText(e)),
  });

  if (!c) return <div className="p-8 text-sm text-zinc-400">Loading…</div>;

  return (
    <div className="p-8 space-y-6 fade-up" data-testid="client-detail-page">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="font-heading text-2xl font-medium tracking-tight text-zinc-900 flex items-center gap-2">
            {c.name} {c.tx_franchise && <Star size={16} weight="fill" className="text-amber-500" title="TX Franchise" />}
          </h1>
          <p className="text-sm text-zinc-500 mt-1">{c.entity_type} · {c.email} · {c.phone} · <span className="font-mono text-xs">{(c.states || []).join(", ")}</span></p>
        </div>
        <div className="flex items-center gap-2">
          <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
            <DialogTrigger asChild>
              <Button data-testid="invite-portal-btn" size="sm" variant="outline"
                onClick={() => setInviteForm({ email: c.email || "", name: c.name || "" })}>
                <EnvelopeSimpleOpen size={14} className="mr-1" /> Invite portal access
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="font-heading">Invite {c.name} to activate their portal</DialogTitle>
              </DialogHeader>
              <p className="text-xs text-zinc-500 -mt-2">We'll email a one-time activation link. The client picks their own password — no temporary password is ever sent in cleartext.</p>
              <div className="space-y-3 mt-3">
                <div>
                  <label className="text-[11px] text-zinc-500 uppercase tracking-wider">Email</label>
                  <Input data-testid="invite-email-input" placeholder="client@example.com" value={inviteForm.email}
                    onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })} />
                </div>
                <div>
                  <label className="text-[11px] text-zinc-500 uppercase tracking-wider">Display name</label>
                  <Input data-testid="invite-name-input" placeholder="Full name" value={inviteForm.name}
                    onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })} />
                </div>
                <Button data-testid="invite-send-btn" className="w-full"
                  disabled={invitePortal.isPending || !inviteForm.email}
                  onClick={() => invitePortal.mutate()}>
                  <EnvelopeSimpleOpen size={14} className="mr-1" />
                  {invitePortal.isPending ? "Sending…" : "Send activation link"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
          <Button data-testid="resend-welcome-btn" size="sm" variant="outline" disabled={resendWelcome.isPending} onClick={() => resendWelcome.mutate()}><PaperPlaneTilt size={14} className="mr-1" /> Resend welcome</Button>
          <Dialog open={engOpen} onOpenChange={setEngOpen}>
            <DialogTrigger asChild><Button data-testid="add-engagement-btn" size="sm"><Plus size={14} className="mr-1" /> New engagement</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-heading">New engagement</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <Select value={engForm.service_type} onValueChange={(v) => setEngForm({ ...engForm, service_type: v })}>
                  <SelectTrigger data-testid="engagement-service-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{SERVICE_TYPES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
                <Input data-testid="engagement-year-input" placeholder="Tax year" value={engForm.tax_year} onChange={(e) => setEngForm({ ...engForm, tax_year: e.target.value })} />
                <Input data-testid="engagement-deadline-input" type="date" value={engForm.deadline} onChange={(e) => setEngForm({ ...engForm, deadline: e.target.value })} />
                <Button data-testid="engagement-save-btn" className="w-full" disabled={createEng.isPending} onClick={() => createEng.mutate()}>Create engagement</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Tabs defaultValue="engagements">
        <TabsList>
          <TabsTrigger value="engagements" data-testid="tab-engagements">Engagements ({c.engagements?.length || 0})</TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents">Documents ({c.documents?.length || 0})</TabsTrigger>
          <TabsTrigger value="messages" data-testid="tab-messages">Messages ({c.messages?.length || 0})</TabsTrigger>
          <TabsTrigger value="esign" data-testid="tab-esign">E-Sign ({c.envelopes?.length || 0})</TabsTrigger>
          <TabsTrigger value="dossier" data-testid="tab-dossier">Dossier</TabsTrigger>
          <TabsTrigger value="portal-tabs" data-testid="tab-portal-tabs">Portal Tabs</TabsTrigger>
        </TabsList>

        <TabsContent value="engagements" className="space-y-2 mt-4">
          {(c.engagements || []).map((e) => (
            <div key={e.id} data-testid={`engagement-card-${e.id}`} className="rounded-lg border border-zinc-200 bg-white p-4 flex items-center justify-between hover:shadow-md transition-shadow duration-200">
              <div>
                <p className="text-sm font-medium text-zinc-900">{e.service_type} · <span className="font-mono">{e.tax_year}</span></p>
                <p className="text-xs text-zinc-500 mt-0.5">Deadline <span className="font-mono">{e.deadline || "—"}</span></p>
              </div>
              <div className="flex items-center gap-2">
                <Link to={`/engagements/${e.id}/data-room`} data-testid={`data-room-link-${e.id}`}
                  className="flex items-center gap-1 text-[11px] text-emerald-800 border border-emerald-200 bg-emerald-50 rounded-md px-2.5 py-1 hover:bg-emerald-100 transition-colors duration-150">
                  <Sparkle size={11} weight="fill" /> Tax Data Room
                </Link>
                <span className="text-xs bg-zinc-900 text-white rounded-sm px-2 py-1">{stageLabel(e.stage)}</span>
              </div>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="documents" className="space-y-2 mt-4">
          {(c.documents || []).map((d) => (
            <div key={d.id} className="rounded-lg border border-zinc-200 bg-white p-3 flex items-center justify-between">
              <div className="min-w-0">
                <p className="text-sm text-zinc-900 truncate font-mono">{d.filename}</p>
                <p className="text-xs text-zinc-500">{d.summary || "Processing…"} · v{d.version}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className={`text-[10px] px-1.5 py-0.5 rounded-sm ${confBadge(d.confidence)}`}>{d.doc_type || d.status}</span>
                <button data-testid={`cd-preview-btn-${d.id}`} title="Preview / download" onClick={() => setPreviewDoc(d)} className="text-zinc-400 hover:text-zinc-900 transition-colors duration-150"><Eye size={15} /></button>
              </div>
            </div>
          ))}
          {c.documents?.length === 0 && <p className="text-xs text-zinc-400 italic">No documents yet.</p>}
          <DocPreviewDialog doc={previewDoc} open={!!previewDoc} onOpenChange={(o) => !o && setPreviewDoc(null)} />
        </TabsContent>

        <TabsContent value="messages" className="mt-4">
          <div className="rounded-lg border border-zinc-200 bg-white p-4 space-y-3 max-h-96 overflow-y-auto">
            {(c.messages || []).map((m) => (
              <div key={m.id} className={`max-w-md ${m.sender_role === "client" ? "" : "ml-auto"}`}>
                <p className={`text-sm rounded-md px-3 py-2 ${m.sender_role === "client" ? "bg-zinc-100 text-zinc-800" : "bg-zinc-900 text-white"}`}>{m.body}</p>
                <p className="text-[10px] text-zinc-400 mt-1">{m.sender_name} · {new Date(m.created_at).toLocaleString()}</p>
              </div>
            ))}
          </div>
          <div className="flex gap-2 mt-3">
            <Input data-testid="message-input" placeholder="Message the client…" value={msg} onChange={(e) => setMsg(e.target.value)} onKeyDown={(e) => e.key === "Enter" && msg && sendMsg.mutate()} />
            <Button data-testid="message-send-btn" disabled={!msg || sendMsg.isPending} onClick={() => sendMsg.mutate()}><PaperPlaneTilt size={14} /></Button>
          </div>
        </TabsContent>

        <TabsContent value="esign" className="space-y-3 mt-4">
          <Dialog open={esignOpen} onOpenChange={setEsignOpen}>
            <DialogTrigger asChild><Button data-testid="request-esign-btn" size="sm" variant="outline"><PenNib size={14} className="mr-1" /> Request e-signature</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-heading">Request e-signature</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <Select value={esignForm.engagement_id} onValueChange={(v) => setEsignForm({ ...esignForm, engagement_id: v })}>
                  <SelectTrigger data-testid="esign-engagement-select"><SelectValue placeholder="Select engagement" /></SelectTrigger>
                  <SelectContent>{(c.engagements || []).map((e) => <SelectItem key={e.id} value={e.id}>{e.service_type} · {e.tax_year}</SelectItem>)}</SelectContent>
                </Select>
                <Input data-testid="esign-title-input" placeholder="Document title" value={esignForm.title} onChange={(e) => setEsignForm({ ...esignForm, title: e.target.value })} />
                <Select value={esignForm.doc_kind} onValueChange={(v) => setEsignForm({ ...esignForm, doc_kind: v })}>
                  <SelectTrigger data-testid="esign-kind-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="8879">Form 8879 (e-file authorization)</SelectItem>
                    <SelectItem value="engagement_letter">Engagement letter</SelectItem>
                    <SelectItem value="other">Other document</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={esignForm.sign_method} onValueChange={(v) => setEsignForm({ ...esignForm, sign_method: v })}>
                  <SelectTrigger data-testid="esign-method-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="in_app">In-app signature (client signs in the portal)</SelectItem>
                    <SelectItem value="drake_portals">Via Drake Portals (link-out, staff confirms)</SelectItem>
                  </SelectContent>
                </Select>
                {esignForm.sign_method === "drake_portals" && (
                  <Input data-testid="esign-link-input" placeholder="Drake Portals signing link (optional)" value={esignForm.external_link} onChange={(e) => setEsignForm({ ...esignForm, external_link: e.target.value })} />
                )}
                <Button data-testid="esign-save-btn" className="w-full" disabled={!esignForm.engagement_id || !esignForm.title || createEnvelope.isPending} onClick={() => createEnvelope.mutate()}>Send signature request</Button>
              </div>
            </DialogContent>
          </Dialog>
          {(c.envelopes || []).map((env) => (
            <div key={env.id} data-testid={`firm-envelope-${env.id}`} className="rounded-lg border border-zinc-200 bg-white p-4 flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-zinc-900">{env.title}</p>
                <p className="text-xs text-zinc-500 mt-0.5">
                  {env.sign_method === "drake_portals" ? "Drake Portals link-out" : "In-app signature"}
                  {env.status === "signed" && ` · signed ${new Date(env.signed_at).toLocaleDateString()}`}
                  {env.signature?.typed_name && ` by "${env.signature.typed_name}" (IP ${env.signature.ip})`}
                </p>
              </div>
              {env.status === "signed" ? (
                <span className="text-[10px] bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-sm px-2 py-1 flex items-center gap-1"><CheckCircle size={12} weight="fill" /> SIGNED</span>
              ) : env.sign_method === "drake_portals" ? (
                <Button data-testid={`mark-signed-btn-${env.id}`} size="sm" variant="outline" className="h-7 text-xs" disabled={markSigned.isPending} onClick={() => markSigned.mutate(env.id)}>Mark signed (Drake confirmed)</Button>
              ) : (
                <span className="text-[10px] bg-amber-50 text-amber-700 border border-amber-200 rounded-sm px-2 py-1">AWAITING CLIENT</span>
              )}
            </div>
          ))}
          {c.envelopes?.length === 0 && <p className="text-xs text-zinc-400 italic">No signature requests yet.</p>}
        </TabsContent>

        <TabsContent value="dossier" className="mt-4">
          <PriorYearImport clientId={id} />
          <ComplianceLockCard clientId={id} />
          <DossierPane clientId={id} />
        </TabsContent>

        <TabsContent value="portal-tabs" className="mt-4">
          <PortalTabOrderPanel clientId={id} clientName={c.name} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
